import React from 'react';
import {
  Text,
  Button,
  Card,
  makeStyles,
  shorthands,
  tokens,
} from '@fluentui/react-components';
import { 
  Rocket20Regular, 
  TableSimple20Regular,
  Navigation20Regular,
} from '@fluentui/react-icons';
import { ApprovedDomain, AssignmentMode } from './types';

const useStyles = makeStyles({
  container: {
    display: 'flex',
    flexDirection: 'column',
    ...shorthands.gap('24px'),
  },
  header: {
    marginBottom: '8px',
  },
  title: {
    fontSize: '20px',
    fontWeight: '600',
    color: tokens.colorNeutralForeground1,
  },
  subtitle: {
    fontSize: '14px',
    color: tokens.colorNeutralForeground3,
    marginTop: '4px',
  },
  modeCard: {
    ...shorthands.padding('20px'),
    ...shorthands.border('2px', 'solid', tokens.colorNeutralStroke1),
    ...shorthands.borderRadius(tokens.borderRadiusMedium),
    cursor: 'pointer',
    transition: 'all 0.2s ease',
    ':hover': {
      ...shorthands.borderColor(tokens.colorBrandStroke1),
      backgroundColor: tokens.colorNeutralBackground1Hover,
      transform: 'translateY(-2px)',
      boxShadow: tokens.shadow8,
    },
    ':active': {
      transform: 'translateY(0px)',
    },
  },
  modeHeader: {
    display: 'flex',
    alignItems: 'center',
    ...shorthands.gap('12px'),
    marginBottom: '12px',
  },
  modeIcon: {
    fontSize: '24px',
    color: tokens.colorBrandForeground1,
  },
  modeTitle: {
    fontSize: '16px',
    fontWeight: '600',
    color: tokens.colorNeutralForeground1,
  },
  modeDescription: {
    fontSize: '13px',
    color: tokens.colorNeutralForeground3,
    lineHeight: '1.5',
  },
  actions: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginTop: '16px',
  },
});

interface ModeSelectionScreenProps {
  domains: ApprovedDomain[];
  onModeSelect: (mode: 'quick' | 'step-by-step' | 'matrix') => void;
  onSkip: () => void;
}

export const ModeSelectionScreen: React.FC<ModeSelectionScreenProps> = ({
  domains,
  onModeSelect,
  onSkip,
}) => {
  const styles = useStyles();

  return (
    <div className={styles.container} data-testid="assignment-mode-selection">
      <div className={styles.header} data-testid="assignment-mode-selection-header">
        <Text className={styles.subtitle} data-testid="assignment-mode-selection-subtitle">
          How would you like to assign these domains to policies?
        </Text>
      </div>

      {/* Quick Assignment Card */}
      <Card 
        className={styles.modeCard}
        onClick={() => onModeSelect('quick')}
        data-testid="assignment-mode-quick"
      >
        <div className={styles.modeHeader} data-testid="assignment-mode-quick-header">
          <Rocket20Regular className={styles.modeIcon} data-testid="assignment-mode-quick-icon" />
          <Text className={styles.modeTitle} data-testid="assignment-mode-quick-title">
            Quick Assignment (Recommended)
          </Text>
        </div>
        
        <Text className={styles.modeDescription} data-testid="assignment-mode-quick-description">
          Assign {domains.length} selected domain{domains.length === 1 ? '' : 's'} to the same policies at once. The fastest way to get started.
        </Text>
      </Card>

      {/* Step-by-Step Assignment Card */}
      <Card 
        className={styles.modeCard}
        onClick={() => onModeSelect('step-by-step')}
        data-testid="assignment-mode-step-by-step"
      >
        <div className={styles.modeHeader} data-testid="assignment-mode-step-by-step-header">
          <Navigation20Regular className={styles.modeIcon} data-testid="assignment-mode-step-by-step-icon" />
          <Text className={styles.modeTitle} data-testid="assignment-mode-step-by-step-title">
            Domain-by-Domain Assignment
          </Text>
        </div>
        
        <Text className={styles.modeDescription} data-testid="assignment-mode-step-by-step-description">
          Guided wizard to assign domains to your managed policies.
        </Text>
      </Card>

      {/* Matrix Assignment Card */}
      <Card 
        className={styles.modeCard}
        onClick={() => onModeSelect('matrix')}
        data-testid="assignment-mode-matrix"
      >
        <div className={styles.modeHeader} data-testid="assignment-mode-matrix-header">
          <TableSimple20Regular className={styles.modeIcon} data-testid="assignment-mode-matrix-icon" />
          <Text className={styles.modeTitle} data-testid="assignment-mode-matrix-title">
            Grid View Assignment
          </Text>
        </div>
        
        <Text className={styles.modeDescription} data-testid="assignment-mode-matrix-description">
          See domains and policies in a grid to quickly assign domains with flexibility.
        </Text>
      </Card>

      <div className={styles.actions} data-testid="assignment-mode-selection-actions">
        <Button appearance="subtle" onClick={onSkip} data-testid="assignment-mode-skip-close">
          Skip Assignment & Close
        </Button>
      </div>
    </div>
  );
};
